import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { SelectChangeEvent } from '@mui/material/Select';

import { MultipleSearchSelect } from '../../../shared/uiComponents/Dropdown/searchSelect';
import { MultipleSelect } from '../../../shared/uiComponents';
import { DataProperties } from '../../../shared/uiComponents/Radio';
import { SelectDataProperties } from '../../../shared/uiComponents/Dropdown';
import { TimeInputsWrapper } from '../helpers';

import { DispatchProperties, useSelector } from '../../../redux/store';
import { getUsers } from '../../../redux/State/userSlice';
import { getSections } from '../../../redux/State/identitySlice/roleSlice';
import { getInsuranceAuthorizationSessionTypes } from '../../../redux/State/clientSlice/insuranceAuthorizationSlice';
import {
	setAuthorizationSessionTypeIds,
	setProviderIds,
	setRoleSections,
} from '../../../redux/State/clientSlice/billingSlice';

const UtilizationReportFilters = () => {
	const dispatch = useDispatch<DispatchProperties>();

	const { providerIds, roleSections, authorizationSessionTypeIds } = useSelector(
		(state) => state.billing.billingFilters,
	);

	const [providersData, setProvidersData] = useState<DataProperties[]>([]);
	const [sectionsData, setSectionsData] = useState<SelectDataProperties[]>([]);
	const [sessionTypesData, setSessionTypesData] = useState<SelectDataProperties[]>([]);
	const [searchStringProvider, setSearchStringProvider] = useState<string>('');
	const [pageSizeProvider, setPageSizeProvider] = useState<number>(10);
	const [hasMoreProvider, setHasMoreProvider] = useState<boolean>(false);

	useEffect(() => {
		dispatch(getSections())
			.then(unwrapResult)
			.then((response) => {
				if (!response) return;
				const data: SelectDataProperties[] = response.map((section: { id: number; name: string }) => ({
					id: section.id.toString(),
					name: section.name,
				}));
				setSectionsData(data);
			});

		dispatch(getInsuranceAuthorizationSessionTypes())
			.then(unwrapResult)
			.then((response) => {
				if (!response) return;
				const data: SelectDataProperties[] = response
					.filter(
						(x: { id: string; name: string; funderId?: string | null }) =>
							!x.name.toLowerCase().includes('non-billable') && !x.funderId,
					)
					.map((type: { id: string; name: string }) => ({
						id: type.id,
						name: type.name,
					}));
				setSessionTypesData(data);
			});
	}, [dispatch]);

	useEffect(() => {
		const timeout = setTimeout(() => {
			dispatch(
				getUsers({
					page: '1',
					pageSize: `${pageSizeProvider}`,
					searchString: searchStringProvider,
				}),
			)
				.then(unwrapResult)
				.then((response) => {
					const { query, hasNextPage } = response;
					if (!query) return;
					const data: DataProperties[] = query.map((user) => ({
						id: user.id,
						label: user.fullName,
					}));
					setProvidersData(data);
					setHasMoreProvider(Boolean(hasNextPage));
				});
		}, 1000);

		// TODO - return void as in return () => void clearTimeout(timeout);

		return () => clearTimeout(timeout);
	}, [dispatch, pageSizeProvider, searchStringProvider]);

	const onProviderIdsChange = (ids: string[]) => {
		dispatch(setProviderIds(ids));
	};

	const onRoleSectionsChange = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		dispatch(setRoleSections(value));
	};

	const onSessionTypesChange = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		dispatch(setAuthorizationSessionTypeIds(value));
	};

	return (
		<TimeInputsWrapper>
			<div style={{ width: '250px' }}>
				<MultipleSearchSelect
					label="Find Providers:"
					values={providerIds}
					data={providersData}
					setValues={onProviderIdsChange}
					searchString={searchStringProvider}
					setSearchString={setSearchStringProvider}
					hasMore={hasMoreProvider}
					setPageSize={setPageSizeProvider}
				/>
			</div>
			<div style={{ width: '200px' }}>
				<MultipleSelect
					label="Role Sections:"
					initialValue={roleSections}
					data={sectionsData}
					setValue={onRoleSectionsChange}
				/>
			</div>
			<div style={{ width: '250px' }}>
				<MultipleSelect
					label="Session Types:"
					initialValue={authorizationSessionTypeIds}
					data={sessionTypesData}
					setValue={onSessionTypesChange}
				/>
			</div>
		</TimeInputsWrapper>
	);
};

export default UtilizationReportFilters;
